/**
 * Gallery Wall Media Gallery Component
 *
 * Custom element for displaying gallery wall product media on the PDP.
 * Handles thumbnail navigation, mock switching, and variant change events.
 */

console.log('[GalleryWallMediaGallery] Script loaded');

// Debug: Global listener to verify event is dispatched
document.addEventListener('gallery-wall:config-changed', (e) => {
  console.log('[GalleryWallMediaGallery] GLOBAL: config-changed event detected', e.detail);
});

class GalleryWallMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.mocks = [];
    this.artworks = [];
    this.totalMockCount = 0;
    this.thumbnailClickHandlers = [];
  }

  connectedCallback() {
    console.log('[GalleryWallMediaGallery] connectedCallback - element initialized');
    this.parseData();
    this.setupEventListeners();
    this.initializeThumbnails();
    this.initializeZoomButtons();
    this.applySquareCropTransforms();
    this.restoreSelectedMock();
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.removeThumbnailListeners();
    this.removeZoomListeners();
  }

  parseData() {
    try {
      this.mocks = JSON.parse(this.dataset.mocks || '[]');
    } catch {
      this.mocks = [];
    }

    // If no mocks data provided, detect mocks from DOM (room + lifestyle)
    if (this.mocks.length === 0) {
      const mainView = this.querySelector('.gallery-wall-media__main');
      if (mainView) {
        const allMockEls = mainView.querySelectorAll('.gallery-wall-media__mock[data-type]');
        allMockEls.forEach((el, index) => {
          this.mocks.push({
            type: el.dataset.type,
            index: index
          });
        });
      }
    }

    try {
      this.artworks = JSON.parse(this.dataset.artworks || '[]');
    } catch {
      this.artworks = [];
    }

    this.productId = this.dataset.productId;
    this.variantId = this.dataset.variantId;
    this.hasArtworks = this.dataset.hasArtworks === 'true';
    // Total count includes all mocks (both lifestyle and room backgrounds)
    this.totalMockCount = this.mocks.length;
  }

  setupEventListeners() {
    // Listen for variant changes (custom event from gallery-wall-sku-picker)
    this.variantChangeHandler = this.handleVariantChange.bind(this);
    document.addEventListener('variant:changed', this.variantChangeHandler);

    // Listen for theme's variant:update event (from standard variant picker)
    this.variantUpdateHandler = this.handleVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.variantUpdateHandler);

    // Listen for gallery wall config changes from SKU picker
    this.configChangeHandler = this.handleConfigChange.bind(this);
    document.addEventListener('gallery-wall:config-changed', this.configChangeHandler);
    console.log('[GalleryWallMediaGallery] Event listeners set up, listening for gallery-wall:config-changed');

    // Sync room mock thumbnail when the main slide's room background loads or changes
    this.roomReadyHandler = (e) => {
      const roomBg = e.detail?.roomBg;
      if (!roomBg) return;
      const source = e.detail?.source;
      // Only apply to thumbnail room mocks (not the source itself)
      const thumbMocks = this.querySelectorAll('.gallery-wall-media__thumbnail room-background-mock');
      thumbMocks.forEach(mock => {
        if (mock !== source && mock.applyRoom) {
          mock.applyRoom(roomBg);
        }
      });
    };
    document.addEventListener('room-background:ready', this.roomReadyHandler);
  }

  removeEventListeners() {
    document.removeEventListener('variant:changed', this.variantChangeHandler);
    document.removeEventListener('variant:update', this.variantUpdateHandler);
    document.removeEventListener('gallery-wall:config-changed', this.configChangeHandler);
    document.removeEventListener('gallery-wall:mocks-loaded', this.mocksLoadedHandler);
    document.removeEventListener('room-background:ready', this.roomReadyHandler);
  }

  initializeThumbnails() {
    const thumbnails = this.querySelectorAll('.gallery-wall-media__thumbnail');

    thumbnails.forEach((thumb, index) => {
      const handler = () => this.selectMock(index);
      thumb.addEventListener('click', handler);
      this.thumbnailClickHandlers.push({ element: thumb, handler });
    });
  }

  removeThumbnailListeners() {
    this.thumbnailClickHandlers.forEach(({ element, handler }) => {
      element.removeEventListener('click', handler);
    });
    this.thumbnailClickHandlers = [];
  }

  initializeZoomButtons() {
    this.zoomClickHandlers = [];
    const zoomBtns = this.querySelectorAll('.gallery-wall-media__zoom-btn');
    zoomBtns.forEach(btn => {
      const handler = (e) => {
        e.stopPropagation();
        // Derive zoom URL from sibling frame mock's artwork URL (upsize to 2000px)
        const frameMock = btn.closest('.gallery-wall-media__mock')?.querySelector('gallery-wall-frame-mock');
        const artworkUrl = frameMock?.dataset?.artworkUrl;
        const title = frameMock?.dataset?.artworkTitle || 'Artwork';
        if (artworkUrl) {
          const zoomUrl = artworkUrl.replace(/width=\d+/, 'width=2000');
          this.openZoomOverlay(zoomUrl, title);
        }
      };
      btn.addEventListener('click', handler);
      this.zoomClickHandlers.push({ element: btn, handler });
    });
  }

  removeZoomListeners() {
    if (this.zoomClickHandlers) {
      this.zoomClickHandlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler);
      });
      this.zoomClickHandlers = [];
    }
    this.closeZoomOverlay();
  }

  openZoomOverlay(url, title) {
    // Create overlay if it doesn't exist
    if (!this._zoomOverlay) {
      const overlay = document.createElement('div');
      overlay.className = 'gallery-wall-zoom-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML = `
        <div class="gallery-wall-zoom-overlay__header">
          <span class="gallery-wall-zoom-overlay__title"></span>
          <button type="button" class="gallery-wall-zoom-overlay__close" aria-label="Close zoom">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="gallery-wall-zoom-overlay__content">
          <img class="gallery-wall-zoom-overlay__image" alt="" />
        </div>
      `;

      // Close on backdrop click (content area, not on the image itself)
      overlay.querySelector('.gallery-wall-zoom-overlay__content').addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.closeZoomOverlay();
      });

      // Close button
      overlay.querySelector('.gallery-wall-zoom-overlay__close').addEventListener('click', () => {
        this.closeZoomOverlay();
      });

      // Close on Escape
      this._zoomKeyHandler = (e) => {
        if (e.key === 'Escape') this.closeZoomOverlay();
      };

      this._zoomOverlay = overlay;
    }

    // Set content
    const img = this._zoomOverlay.querySelector('.gallery-wall-zoom-overlay__image');
    const titleEl = this._zoomOverlay.querySelector('.gallery-wall-zoom-overlay__title');
    img.src = url;
    img.alt = title;
    titleEl.textContent = title;
    this._zoomOverlay.setAttribute('aria-label', `Zoom: ${title}`);

    // Show overlay
    document.body.appendChild(this._zoomOverlay);
    document.addEventListener('keydown', this._zoomKeyHandler);
    document.body.style.overflow = 'hidden';

    // Trigger transition
    requestAnimationFrame(() => {
      this._zoomOverlay.classList.add('gallery-wall-zoom-overlay--visible');
    });

    // Focus close button for accessibility
    const closeBtn = this._zoomOverlay.querySelector('.gallery-wall-zoom-overlay__close');
    if (closeBtn) closeBtn.focus();
  }

  closeZoomOverlay() {
    if (!this._zoomOverlay || !this._zoomOverlay.parentNode) return;

    this._zoomOverlay.classList.remove('gallery-wall-zoom-overlay--visible');
    document.removeEventListener('keydown', this._zoomKeyHandler);
    document.body.style.overflow = '';

    // Remove after transition
    const overlay = this._zoomOverlay;
    const onEnd = () => {
      overlay.removeEventListener('transitionend', onEnd);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    };
    overlay.addEventListener('transitionend', onEnd);
    // Fallback if transition doesn't fire
    setTimeout(() => {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }, 300);
  }

  /**
   * Apply square crop transforms to room background containers
   * This scales and positions the room mock to show a square crop region
   */
  applySquareCropTransforms() {
    const gallery = this.querySelector('.gallery-wall-media');
    if (!gallery) return;

    // Apply to main image crop containers
    const mainCropContainers = gallery.querySelectorAll('.gallery-wall-media__square-crop');
    mainCropContainers.forEach(container => this.applySquareCropToContainer(container));

    // Apply to thumbnail crop containers
    const thumbCropContainers = gallery.querySelectorAll('.gallery-wall-media__thumbnail-crop');
    thumbCropContainers.forEach(container => this.applySquareCropToContainer(container));
  }

  /**
   * Apply square crop transform to a single container
   */
  applySquareCropToContainer(container) {
    const aspect = parseFloat(container.dataset.aspect) || 1.6;
    const centerX = parseFloat(container.dataset.centerX) || 0.5;
    const centerY = parseFloat(container.dataset.centerY) || 0.5;
    const size = parseFloat(container.dataset.size) || 1.0;

    const child = container.firstElementChild;
    if (!child) return;

    // room-background-mock handles its own square crop via canvas rendering —
    // applying a CSS transform would double-crop, zooming in too far
    if (child.tagName === 'ROOM-BACKGROUND-MOCK') return;

    if (aspect >= 1) {
      // Wide image
      const scale = aspect / size;
      const cropWidth = size / aspect;
      const cropLeft = centerX - cropWidth / 2;
      const cropTop = centerY - size / 2;
      const translateX = -scale * cropLeft * 100;
      const translateY = -scale * cropTop * 100;
      child.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
    } else {
      // Tall image (aspect < 1)
      const scale = 1 / (aspect * size);
      const cropHeight = size * aspect;
      const cropLeft = centerX - size / 2;
      const cropTop = centerY - cropHeight / 2;
      const translateX = -scale * cropLeft * 100;
      const translateY = -scale * cropTop * 100;
      child.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
    }
  }

  restoreSelectedMock() {
    const storedIndex = sessionStorage.getItem('gallery-wall-selected-mock');
    if (storedIndex !== null) {
      const index = parseInt(storedIndex, 10);
      if (!isNaN(index) && index >= 0 && index < this.totalMockCount && index !== 0) {
        this.selectMock(index);
      }
    }
  }

  selectMock(index) {
    if (index === this.currentIndex) return;
    if (index < 0 || index >= this.totalMockCount) return;

    const mock = this.mocks[index];
    this.currentIndex = index;

    // Update thumbnail states
    const thumbnails = this.querySelectorAll('.gallery-wall-media__thumbnail');
    thumbnails.forEach((thumb, i) => {
      thumb.classList.toggle('gallery-wall-media__thumbnail--active', i === index);
    });

    // Update main view - show the selected mock, hide others
    this.renderMock(index);

    // Hide title chip on last slide (info slide with text)
    const titleChip = this.querySelector('.pdp-title-chip');
    if (titleChip) {
      const isLastSlide = index === this.totalMockCount - 1;
      titleChip.toggleAttribute('hidden', isLastSlide);
    }

    // Dispatch event for analytics
    this.dispatchEvent(new CustomEvent('gallery-wall:mock-selected', {
      bubbles: true,
      detail: { index, mock }
    }));
  }

  renderMock(index) {
    const mainView = this.querySelector('.gallery-wall-media__main');
    if (!mainView) return;

    // Get all pre-rendered mocks
    const allMocks = mainView.querySelectorAll('.gallery-wall-media__mock');

    // Toggle active state based on index
    allMocks.forEach((mockEl, i) => {
      if (i === index) {
        mockEl.classList.add('gallery-wall-media__mock--active');
      } else {
        mockEl.classList.remove('gallery-wall-media__mock--active');
      }
    });

    // Store selected mock index for persistence across page loads
    sessionStorage.setItem('gallery-wall-selected-mock', index.toString());
  }

  handleVariantChange(event) {
    const { variantId, productId } = event.detail || {};

    // Only handle events for this product (compare as strings to avoid type mismatch)
    if (productId && String(productId) !== String(this.productId)) return;

    if (variantId && String(variantId) !== String(this.variantId)) {
      this.variantId = String(variantId);

      // Reload the media gallery section with new variant's configuration
      this.reloadSection(variantId);

      // Dispatch event for other listeners
      this.dispatchEvent(new CustomEvent('gallery-wall:variant-updated', {
        bubbles: true,
        detail: { variantId, productId: this.productId }
      }));
    }
  }

  /**
   * Handle theme's variant:update event (from standard variant picker)
   * Event structure: { detail: { resource: { id }, data: { productId } } }
   */
  handleVariantUpdate(event) {
    const { resource, data } = event.detail || {};
    const variantId = resource?.id;
    const productId = data?.productId;

    // Only handle events for this product (compare as strings to avoid type mismatch)
    if (productId && String(productId) !== String(this.productId)) return;

    if (variantId && String(variantId) !== String(this.variantId)) {
      this.variantId = String(variantId);

      // Reload the media gallery section with new variant's configuration
      this.reloadSection(variantId);

      // Dispatch event for other listeners
      this.dispatchEvent(new CustomEvent('gallery-wall:variant-updated', {
        bubbles: true,
        detail: { variantId, productId: this.productId }
      }));
    }
  }

  /**
   * Reload the media gallery section using Shopify's Section Rendering API
   * This ensures the Liquid template re-renders with the new variant's configuration
   */
  async reloadSection(variantId) {
    // Find the section ID from a parent section element
    const section = this.closest('[id^="shopify-section-"]');
    if (!section) {
      console.warn('GalleryWallMediaGallery: Could not find parent section for reload');
      return;
    }

    const sectionId = section.id.replace('shopify-section-', '');

    try {
      // Build URL for section rendering
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error(`Section fetch failed: ${response.status}`);
      }

      const html = await response.text();

      // Parse the response and extract the new gallery content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGallery = doc.querySelector('gallery-wall-media-gallery');

      if (newGallery) {
        // Store current mock selection before replacing
        const currentMockIndex = this.currentIndex;

        // Replace inner content
        this.innerHTML = newGallery.innerHTML;

        // Update data attributes
        for (const attr of newGallery.attributes) {
          if (attr.name.startsWith('data-')) {
            this.setAttribute(attr.name, attr.value);
          }
        }

        // Re-initialize
        this.removeThumbnailListeners();
        this.removeZoomListeners();
        this.parseData();
        this.initializeThumbnails();
        this.initializeZoomButtons();

        // Restore mock selection if still valid
        if (currentMockIndex > 0 && currentMockIndex < this.totalMockCount) {
          this.selectMock(currentMockIndex);
        }
      }
    } catch (error) {
      console.error('GalleryWallMediaGallery: Failed to reload section', error);
    }
  }

  handleConfigChange(event) {
    const {
      frameColor, frameColorHex, mouldingTile, mouldingWidth, border, borderOption, hasFrame, borderWidth,
      positions, wallDimensions, positionId, frameConfigs, isInitialization
    } = event.detail || {};

    console.log('[GalleryWallMediaGallery] Config change received:', event.detail);

    // Build config object for updates
    const config = {};
    if (frameColorHex) config.frameColor = frameColorHex;
    else if (frameColor) config.frameColor = frameColor; // Fallback to handle if no hex
    if (mouldingTile !== undefined) config.mouldingTile = mouldingTile;
    if (mouldingWidth !== undefined && mouldingWidth !== null) config.mouldingWidth = mouldingWidth;
    if (hasFrame !== undefined) config.hasFrame = hasFrame;
    if (borderWidth !== undefined) config.borderWidth = borderWidth;
    // borderOption from Svelte dispatchers, border from SKU picker — use whichever is available
    const effectiveBorderOption = borderOption ?? border;
    if (effectiveBorderOption !== undefined) config.borderOption = effectiveBorderOption;
    if (positions !== undefined) config.positions = positions;
    if (wallDimensions !== undefined) config.wallDimensions = wallDimensions;
    if (positionId !== undefined) config.positionId = positionId;
    // Pass through frameConfigs for initialization/batch updates
    if (frameConfigs !== undefined) config.frameConfigs = frameConfigs;

    console.log('[GalleryWallMediaGallery] Built config:', config, 'hasFrameConfigs:', !!config.frameConfigs);

    // Handle frame mock updates based on change type
    const frameMocks = this.querySelectorAll('gallery-wall-frame-mock');

    if (positionId) {
      // Per-position change: update that artwork's frame mock and thumbnail
      const frameMockSlide = this.querySelector(
        `.gallery-wall-media__mock[data-type="frame"][data-position-id="${positionId}"]`
      );
      if (frameMockSlide) {
        const frameMock = frameMockSlide.querySelector('gallery-wall-frame-mock');
        if (frameMock && frameMock.updateConfig) {
          frameMock.updateConfig(config);
        }
      }
      const thumbMock = this.querySelector(
        `.gallery-wall-media__thumbnail[data-type="frame"][data-position-id="${positionId}"] gallery-wall-frame-mock`
      );
      if (thumbMock && thumbMock.updateConfig) {
        thumbMock.updateConfig(config);
      }
      // If user is already viewing this position's frame mock, stay on it.
      // Otherwise switch to room background so they see the change in context.
      const currentSlideIndex = frameMockSlide ? parseInt(frameMockSlide.dataset.index, 10) : -1;
      if (this.currentIndex !== currentSlideIndex && this.currentIndex !== 0) {
        this.selectMock(0);
      }
    } else if (frameConfigs) {
      // Batch update: iterate per-position configs for main slides and thumbnails
      for (const [posId, posConfig] of Object.entries(frameConfigs)) {
        // Update main slide frame mock
        const mockEl = this.querySelector(
          `.gallery-wall-media__mock[data-type="frame"][data-position-id="${posId}"]`
        );
        if (mockEl) {
          const frameMock = mockEl.querySelector('gallery-wall-frame-mock');
          if (frameMock && frameMock.updateConfig) {
            frameMock.updateConfig(posConfig);
          }
        }
        // Update thumbnail frame mock
        const thumbMock = this.querySelector(
          `.gallery-wall-media__thumbnail[data-type="frame"][data-position-id="${posId}"] gallery-wall-frame-mock`
        );
        if (thumbMock && thumbMock.updateConfig) {
          thumbMock.updateConfig(posConfig);
        }
      }
      if (this.currentIndex !== 0) {
        this.selectMock(0);
      }
    } else {
      // Global change (no positionId): update ALL frame mocks (main + thumbnails), switch to room
      frameMocks.forEach(mock => {
        if (mock.updateConfig) {
          mock.updateConfig(config);
        }
      });
      if (this.currentIndex !== 0) {
        this.selectMock(0);
      }
    }

    // When positions change (size change), compute physical artwork widths and update frame mocks.
    // Positions are in SVG coordinate units; wallDimensions.widthInches gives the physical layout width.
    // svgUnitsPerInch = contentWidth / layoutWidthInches, then physicalWidth = pos.width / svgUnitsPerInch.
    if (positions && Array.isArray(positions) && wallDimensions && wallDimensions.widthInches) {
      const layoutWidthInches = parseFloat(wallDimensions.widthInches);
      if (layoutWidthInches > 0) {
        const minX = Math.min(...positions.map(p => p.x || 0));
        const maxX = Math.max(...positions.map(p => (p.x || 0) + (p.width || p.w || 0)));
        const contentWidth = maxX - minX;
        const svgUnitsPerInch = contentWidth > 0 ? contentWidth / layoutWidthInches : 1;

        const allFrameSlides = this.querySelectorAll('.gallery-wall-media__mock[data-type="frame"]');
        allFrameSlides.forEach((slide, i) => {
          const pos = positions[i];
          if (pos && (pos.width || pos.w)) {
            const artworkWidth = (pos.width || pos.w) / svgUnitsPerInch;
            const mock = slide.querySelector('gallery-wall-frame-mock');
            if (mock && mock.updateConfig) {
              mock.updateConfig({ artworkWidth });
            }
          }
        });
        // Also update thumbnail frame mocks
        const allFrameThumbs = this.querySelectorAll('.gallery-wall-media__thumbnail[data-type="frame"]');
        allFrameThumbs.forEach((thumb, i) => {
          const pos = positions[i];
          if (pos && (pos.width || pos.w)) {
            const artworkWidth = (pos.width || pos.w) / svgUnitsPerInch;
            const mock = thumb.querySelector('gallery-wall-frame-mock');
            if (mock && mock.updateConfig) {
              mock.updateConfig({ artworkWidth });
            }
          }
        });
      }
    }

    // Update room-background-mock components with new config
    const roomMocks = this.querySelectorAll('room-background-mock');
    console.log('[GalleryWallMediaGallery] Found room mocks:', roomMocks.length);

    roomMocks.forEach((mock, i) => {
      if (mock.updateConfig) {
        console.log(`[GalleryWallMediaGallery] Calling updateConfig on mock ${i}`);
        mock.updateConfig(config);
      } else {
        console.log(`[GalleryWallMediaGallery] Mock ${i} has no updateConfig, using fallback`);
        // Fallback: update dataset and re-render manually
        if (config.frameColor) mock.dataset.frameColor = config.frameColor;
        if (config.mouldingTile !== undefined) mock.dataset.mouldingTile = config.mouldingTile || '';
        if (config.hasFrame !== undefined) mock.dataset.hasFrame = String(config.hasFrame);
        if (mock.parseData && mock.render) {
          mock.parseData();
          mock.render();
        }
      }
    });

    // Update art-set-mock components
    const artSetMocks = this.querySelectorAll('art-set-mock');
    artSetMocks.forEach(mock => {
      if (mock.updateConfig) {
        mock.updateConfig(config);
      }
    });
  }

  /**
   * Clear any existing lifestyle mocks from the gallery
   */
  clearLifestyleMocks() {
    // Remove lifestyle mocks from data array
    this.mocks = this.mocks.filter(m => m.type !== 'lifestyle');
    this.totalMockCount = this.mocks.length;

    // Remove lifestyle mock DOM elements
    const mainView = this.querySelector('.gallery-wall-media__main');
    if (mainView) {
      const lifestyleMockEls = mainView.querySelectorAll('.gallery-wall-media__mock[data-type="lifestyle"]');
      lifestyleMockEls.forEach(el => el.remove());
    }

    // Remove lifestyle thumbnails
    const track = this.querySelector('.gallery-wall-media__thumbnails-track');
    if (track) {
      const lifestyleThumbs = track.querySelectorAll('.gallery-wall-media__thumbnail[data-type="lifestyle"]');
      lifestyleThumbs.forEach(el => el.remove());
    }

    // Re-index remaining thumbnails
    if (track) {
      const allThumbnails = track.querySelectorAll('.gallery-wall-media__thumbnail');
      allThumbnails.forEach((thumb, i) => {
        thumb.dataset.index = i.toString();
      });
      this.removeThumbnailListeners();
      this.initializeThumbnails();
    }

    // Reset current index
    this.currentIndex = 0;
    const firstMock = this.querySelector('.gallery-wall-media__mock');
    if (firstMock) {
      firstMock.classList.add('gallery-wall-media__mock--active');
    }
    const firstThumb = this.querySelector('.gallery-wall-media__thumbnail');
    if (firstThumb) {
      firstThumb.classList.add('gallery-wall-media__thumbnail--active');
    }
  }

  /**
   * Handle lifestyle mocks loaded from app block API
   * Lifestyle mocks are stored as separate metaobjects that Liquid can't access,
   * so the app block fetches them via API and dispatches this event.
   */
  handleMocksLoaded(event) {
    const { lifestyleMocks, artworks, productHandle } = event.detail || {};

    // Skip if no lifestyle mocks
    if (!lifestyleMocks || lifestyleMocks.length === 0) return;

    // Skip if we already have lifestyle mocks (prevent duplicate processing)
    const existingLifestyleMocks = this.mocks.filter(m => m.type === 'lifestyle').length;
    if (existingLifestyleMocks > 0) {
      console.log(`[GalleryWallMediaGallery] Skipping - already have ${existingLifestyleMocks} lifestyle mocks`);
      return;
    }

    // Count existing mocks before clearing
    const existingRoomMocks = this.mocks.filter(m => m.type === 'room').length;
    console.log(`[GalleryWallMediaGallery] Before: ${this.mocks.length} mocks (${existingRoomMocks} room backgrounds)`);

    // Remove any existing lifestyle mocks first (prevents duplicates)
    // This preserves room backgrounds (type !== 'lifestyle')
    this.clearLifestyleMocks();

    const roomMocksAfterClear = this.mocks.filter(m => m.type === 'room').length;
    console.log(`[GalleryWallMediaGallery] After clear: ${this.mocks.length} mocks (${roomMocksAfterClear} room backgrounds preserved)`);

    // Convert API format to gallery format and prepend to mocks array
    // API may return imageUrl, image_url, or image.url depending on structure
    const newMocks = lifestyleMocks.map(mock => {
      const imageUrl = mock.imageUrl || mock.image_url || mock.image?.url || mock.url;
      return {
        type: 'lifestyle',
        url: imageUrl,
        thumb: imageUrl, // Will be same URL, browser will cache
        name: mock.name || 'Lifestyle',
        imageUrl: imageUrl, // Keep for renderLifestyleMocks
        imageWidth: mock.imageWidth || mock.image_width || mock.image?.width || 1600,
        imageHeight: mock.imageHeight || mock.image_height || mock.image?.height || 1000,
        framePolygons: mock.framePolygons || mock.frame_polygons || {},
        squareCrop: mock.squareCrop || mock.square_crop || null,
        lightingOpacity: mock.lightingOpacity || mock.lighting_opacity || 0,
        lightingBlend: mock.lightingBlend || mock.lighting_blend || 'multiply'
      };
    });

    // Prepend lifestyle mocks to the beginning (before room backgrounds)
    this.mocks = [...newMocks, ...this.mocks];
    this.totalMockCount = this.mocks.length;

    // Update data attribute for consistency
    this.dataset.mocks = JSON.stringify(this.mocks);

    // Re-render the gallery with new mocks
    this.renderLifestyleMocks(newMocks, artworks);

    // Log final state
    const finalRoomMocks = this.mocks.filter(m => m.type === 'room').length;
    const finalLifestyleMocks = this.mocks.filter(m => m.type === 'lifestyle').length;
    console.log(`[GalleryWallMediaGallery] Final: ${this.totalMockCount} mocks (${finalLifestyleMocks} lifestyle + ${finalRoomMocks} room backgrounds)`);
  }

  /**
   * Dynamically render lifestyle mock elements and thumbnails
   */
  renderLifestyleMocks(lifestyleMocks, artworks = []) {
    const mainView = this.querySelector('.gallery-wall-media__main');
    if (!mainView) return;

    // Get existing mocks to insert before
    const existingMocks = mainView.querySelectorAll('.gallery-wall-media__mock');
    const firstExistingMock = existingMocks[0];

    // Create thumbnail container if it doesn't exist
    let thumbnailsContainer = this.querySelector('.gallery-wall-media__thumbnails');
    if (!thumbnailsContainer && this.totalMockCount > 1) {
      thumbnailsContainer = document.createElement('div');
      thumbnailsContainer.className = 'gallery-wall-media__thumbnails';
      const track = document.createElement('div');
      track.className = 'gallery-wall-media__thumbnails-track';
      thumbnailsContainer.appendChild(track);
      this.appendChild(thumbnailsContainer);
    }
    const track = thumbnailsContainer?.querySelector('.gallery-wall-media__thumbnails-track');

    // Get existing thumbnails to insert before
    const existingThumbnails = track?.querySelectorAll('.gallery-wall-media__thumbnail');
    const firstExistingThumb = existingThumbnails?.[0];

    // Remove active state from ALL existing mocks and thumbnails
    existingMocks.forEach(mock => {
      mock.classList.remove('gallery-wall-media__mock--active');
    });
    existingThumbnails?.forEach(thumb => {
      thumb.classList.remove('gallery-wall-media__thumbnail--active');
    });

    // Build artworks JSON for art-set-mock component
    const artworksJson = JSON.stringify(artworks.map(a => ({
      url: a.imageUrl || a.url,
      title: a.title || 'Artwork'
    })));

    // Create and insert lifestyle mock elements
    lifestyleMocks.forEach((mock, index) => {
      // Create wrapper div
      const mockEl = document.createElement('div');
      mockEl.className = 'gallery-wall-media__mock';
      mockEl.dataset.type = 'lifestyle';
      if (index === 0) {
        mockEl.classList.add('gallery-wall-media__mock--active');
      }

      // Calculate image aspect ratio (default to 16:10 if not available)
      const imgWidth = mock.imageWidth || 1600;
      const imgHeight = mock.imageHeight || 1000;
      const imgAspect = imgWidth / imgHeight;

      // Create art-set-mock custom element with full inner structure
      const artSetMock = document.createElement('art-set-mock');
      artSetMock.className = 'art-set-mock';
      artSetMock.setAttribute('data-frame-polygons', JSON.stringify(mock.framePolygons || {}));
      artSetMock.setAttribute('data-artworks', artworksJson);
      artSetMock.setAttribute('data-lighting-opacity', mock.lightingOpacity || 0);
      artSetMock.setAttribute('data-lighting-blend', mock.lightingBlend || 'multiply');
      artSetMock.setAttribute('data-img-aspect', imgAspect);

      // Create the inner structure that the component expects
      const cropWrapper = document.createElement('div');
      cropWrapper.className = 'art-set-mock__crop-wrapper';

      // Background image
      const bgImg = document.createElement('img');
      const mockImageUrl = mock.imageUrl || mock.url;
      bgImg.src = mockImageUrl;
      bgImg.alt = mock.name || 'Gallery wall lifestyle image';
      bgImg.loading = 'lazy';
      bgImg.className = 'art-set-mock__background';
      bgImg.width = imgWidth;
      bgImg.height = imgHeight;
      cropWrapper.appendChild(bgImg);

      // Frames container (populated by JS)
      const framesContainer = document.createElement('div');
      framesContainer.className = 'art-set-mock__frames';
      framesContainer.setAttribute('aria-hidden', 'true');
      cropWrapper.appendChild(framesContainer);

      // Lighting overlay if opacity > 0
      if (mock.lightingOpacity > 0) {
        const lightingImg = document.createElement('img');
        lightingImg.src = mockImageUrl;
        lightingImg.alt = '';
        lightingImg.loading = 'lazy';
        lightingImg.className = 'art-set-mock__lighting';
        lightingImg.style.opacity = mock.lightingOpacity;
        lightingImg.style.mixBlendMode = mock.lightingBlend || 'multiply';
        lightingImg.setAttribute('aria-hidden', 'true');
        cropWrapper.appendChild(lightingImg);
      }

      artSetMock.appendChild(cropWrapper);
      mockEl.appendChild(artSetMock);

      // Insert before existing mocks
      if (firstExistingMock) {
        mainView.insertBefore(mockEl, firstExistingMock);
      } else {
        mainView.appendChild(mockEl);
      }

      // Create thumbnail with art-set-mock for rendered preview
      if (track) {
        const thumbBtn = document.createElement('button');
        thumbBtn.type = 'button';
        thumbBtn.className = 'gallery-wall-media__thumbnail gallery-wall-media__thumbnail--rendered';
        if (index === 0) {
          thumbBtn.classList.add('gallery-wall-media__thumbnail--active');
        }
        thumbBtn.dataset.index = index.toString();
        thumbBtn.dataset.type = 'lifestyle';
        thumbBtn.setAttribute('aria-label', `View ${mock.name || 'lifestyle mock'}`);

        // Create mini art-set-mock for thumbnail with rendered artwork
        const thumbMock = document.createElement('art-set-mock');
        thumbMock.className = 'art-set-mock gallery-wall-media__thumbnail-mock';
        thumbMock.setAttribute('data-frame-polygons', JSON.stringify(mock.framePolygons || {}));
        thumbMock.setAttribute('data-artworks', artworksJson);
        thumbMock.setAttribute('data-lighting-opacity', mock.lightingOpacity || 0);
        thumbMock.setAttribute('data-lighting-blend', mock.lightingBlend || 'multiply');
        thumbMock.setAttribute('data-img-aspect', imgAspect);

        // Create inner structure for thumbnail art-set-mock
        const thumbCropWrapper = document.createElement('div');
        thumbCropWrapper.className = 'art-set-mock__crop-wrapper';

        // Apply square crop transform if available (thumbnails are always 1:1)
        if (mock.squareCrop && mock.squareCrop.size) {
          const transform = this.calculateSquareCropTransform(mock.squareCrop, imgAspect);
          if (transform) {
            thumbCropWrapper.style.transform = transform;
          }
        }

        const thumbBgImg = document.createElement('img');
        thumbBgImg.src = mockImageUrl;
        thumbBgImg.alt = `${mock.name || 'Lifestyle'} thumbnail`;
        thumbBgImg.loading = 'lazy';
        thumbBgImg.className = 'art-set-mock__background';
        thumbCropWrapper.appendChild(thumbBgImg);

        const thumbFramesContainer = document.createElement('div');
        thumbFramesContainer.className = 'art-set-mock__frames';
        thumbFramesContainer.setAttribute('aria-hidden', 'true');
        thumbCropWrapper.appendChild(thumbFramesContainer);

        thumbMock.appendChild(thumbCropWrapper);
        thumbBtn.appendChild(thumbMock);

        // Insert before existing thumbnails
        if (firstExistingThumb) {
          track.insertBefore(thumbBtn, firstExistingThumb);
        } else {
          track.appendChild(thumbBtn);
        }

        // Add click handler
        const handler = () => this.selectMock(index);
        thumbBtn.addEventListener('click', handler);
        this.thumbnailClickHandlers.push({ element: thumbBtn, handler });
      }
    });

    // Update indices for existing thumbnails (they shifted by lifestyleMocks.length)
    if (track) {
      const allThumbnails = track.querySelectorAll('.gallery-wall-media__thumbnail');
      allThumbnails.forEach((thumb, i) => {
        thumb.dataset.index = i.toString();
      });

      // Re-bind click handlers for shifted thumbnails
      this.removeThumbnailListeners();
      this.initializeThumbnails();
    }

    // Reset to first mock (new lifestyle mock)
    this.currentIndex = 0;

    // Verify DOM state
    if (mainView) {
      const allMockEls = mainView.querySelectorAll('.gallery-wall-media__mock');
      const lifestyleEls = mainView.querySelectorAll('.gallery-wall-media__mock[data-type="lifestyle"]');
      const roomEls = mainView.querySelectorAll('.gallery-wall-media__mock[data-type="room"]');
      console.log(`[GalleryWallMediaGallery] DOM: ${allMockEls.length} mock elements (${lifestyleEls.length} lifestyle + ${roomEls.length} room)`);
    }
  }

  /**
   * Get current selected options for the gallery wall
   * Used by SKU picker to sync state
   */
  getSelectedOptions() {
    return {
      mockIndex: this.currentIndex,
      variantId: this.variantId
    };
  }

  /**
   * Calculate CSS transform for square crop of a wide image.
   * Used for thumbnails which are always displayed in 1:1 aspect ratio.
   *
   * @param {Object} squareCrop - The square crop parameters { centerX, centerY, size }
   * @param {number} imgAspect - The original image aspect ratio (width/height)
   * @returns {string|null} CSS transform value or null if invalid
   */
  calculateSquareCropTransform(squareCrop, imgAspect) {
    const { centerX, centerY, size } = squareCrop;

    if (!size || size <= 0 || !imgAspect) {
      return null;
    }

    let scale, cropLeft, cropTop;

    if (imgAspect >= 1) {
      // Wide image (e.g., 16:9 or 16:10)
      // When displayed in a 1:1 container with object-fit: contain,
      // the image is letterboxed with black bars top/bottom
      const displayedHeightRatio = 1 / imgAspect;
      const yOffset = (1 - displayedHeightRatio) / 2;

      // Scale to fill the square with the crop region
      scale = 1 / (size * displayedHeightRatio);

      // Calculate crop position (normalized to container)
      const cropWidthNorm = size / imgAspect;
      cropLeft = centerX - cropWidthNorm / 2;
      cropTop = yOffset + (centerY - size / 2) * displayedHeightRatio;
    } else {
      // Tall image (portrait)
      const displayedWidthRatio = imgAspect;
      const xOffset = (1 - displayedWidthRatio) / 2;

      scale = 1 / (size * displayedWidthRatio);
      const cropHeightNorm = size / imgAspect;
      cropLeft = xOffset + (centerX - size / 2) * displayedWidthRatio;
      cropTop = centerY - cropHeightNorm / 2;
    }

    const translateX = -cropLeft * 100;
    const translateY = -cropTop * 100;

    return `scale(${scale}) translate(${translateX}%, ${translateY}%)`;
  }
}

// Register custom element
if (!customElements.get('gallery-wall-media-gallery')) {
  customElements.define('gallery-wall-media-gallery', GalleryWallMediaGallery);
}
